import React, { FC, JSX } from "react";
import { Link } from "react-router-dom";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import Avatar from "../../components/ui/Avatar";
import { AgentProfileFormData } from "../../types/components.d";

interface AgentProfileSummaryProps {
  // No props defined in the original component, but keeping the interface for consistency
}

const AgentProfileSummary: FC<AgentProfileSummaryProps> = (): JSX.Element => {
  // Mock data - in real app, this would come from API/context
  const agentData: AgentProfileFormData & {
    profilePhoto: string | null;
    verificationStatus: "verified" | "pending" | "rejected";
    profileCompletion: number;
  } = {
    firstName: "Sarah",
    lastName: "Johnson",
    email: "sarah.johnson@elitetalent.com",
    phone: "+91 98765 43210",
    dateOfBirth: "1990-01-01",
    gender: "Female",
    address: "Mumbai, India",
    agencyName: "Elite Talent Solutions",
    experience: "8",
    specializations: ["IT Recruitment", "Healthcare", "Finance", "Engineering"],
    services: [
      "Executive Search",
      "Permanent Placement",
      "Contract Staffing",
      "Temporary Staffing",
      "RPO Services",
      "Consulting",
    ],
    feeStructure: "15% of annual salary for permanent placements",
    credentials:
      "Certified Professional Recruiter, Certified Talent Acquisition Professional",
    portfolio: "https://sarahjohnson.com",
    references: "John Smith, Michael Brown",
    availability: ["Monday to Friday"],
    profilePhoto: null,
    totalPlacements: 230,
    successRate: 92,
    avgSalaryPlaced: "8-15 LPA",
    clientRetentionRate: 88,
    testimonials:
      '"Sarah is an exceptional recruiter who understands our needs perfectly. She has consistently delivered top-quality candidates and has become our go-to partner for all hiring needs." - John Smith, CTO, TechCorp',
    notablePlacements:
      "Successfully placed CTO at a unicorn startup, Head of Engineering at Fortune 500 company, and multiple senior developers at leading tech companies.",
    ref1Name: "Michael Brown",
    ref1Company: "TechCorp Solutions",
    ref1Email: "michael.brown@techcorp.com",
    ref1Phone: "+91 98765 43211",
    ref2Name: "Lisa Davis",
    ref2Company: "HealthTech Innovations",
    ref2Email: "lisa.davis@healthtech.com",
    ref2Phone: "+91 98765 43212",
    profileVisibility: true,
    emailNotifications: true,
    verificationStatus: "verified",
    profileCompletion: 95,
    govtIdFront: undefined,
    govtIdBack: undefined,
    idProof: undefined,
    addressProof: undefined,
    industriesServed:
      "Technology, Healthcare, Finance, Manufacturing, Consulting",
    geographicCoverage: "Mumbai, Delhi, Bangalore, Chennai, Pan India",
    avgTimeToFill: "30-45 days",
    startTime: "09:00",
    endTime: "18:00",
    professionalSummary:
      "Experienced recruitment consultant with 8+ years of expertise in placing top talent across various industries. Specialized in IT and healthcare recruitment with a proven track record of successful placements and client satisfaction.",
    professionalTitle: "Senior Recruitment Consultant",
    experienceDetails: [
      {
        id: 1,
        company: "Elite Talent Solutions",
        position: "Senior Recruitment Consultant",
        startDate: "2020-01-15",
        endDate: "",
        current: true,
        description:
          "Lead recruitment consultant responsible for managing key client accounts and placing senior-level professionals across IT and healthcare sectors.",
        achievements:
          "Achieved 95% client satisfaction rate, placed 150+ candidates, generated ₹2.5Cr in revenue",
      },
      {
        id: 2,
        company: "Global Recruiters Inc",
        position: "Recruitment Consultant",
        startDate: "2018-03-01",
        endDate: "2019-12-31",
        current: false,
        description:
          "Specialized in IT recruitment, working with startups and mid-size companies to fill technical positions.",
        achievements:
          "Placed 80+ IT professionals, maintained 90% placement success rate",
      },
    ],
    certificationsDetails: [
      {
        id: 1,
        name: "Certified Professional Recruiter",
        issuer: "National Association of Personnel Services",
        date: "2020-06-15",
        expiry: "2025-06-15",
        credentialId: "CPR-2020-001",
      },
      {
        id: 2,
        name: "Certified Talent Acquisition Professional",
        issuer: "Talent Acquisition Institute",
        date: "2019-03-20",
        expiry: "2024-03-20",
        credentialId: "CTAP-2019-045",
      },
    ],
  };

  const getVerificationStatusColor = (status: string): string => {
    switch (status) {
      case "verified":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getVerificationStatusText = (status: string): string => {
    switch (status) {
      case "verified":
        return "Verified";
      case "pending":
        return "Pending Review";
      case "rejected":
        return "Rejected";
      default:
        return "Not Submitted";
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Agent Profile
            </h1>
            <p className="text-gray-600">
              Complete overview of your professional profile
            </p>
          </div>
          <div className="flex space-x-3">
            <Link to="/agent/profile/edit">
              <Button variant="outline">Edit Profile</Button>
            </Link>
            <Button>Download Profile</Button>
          </div>
        </div>

        {/* Profile Completion */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Profile Completion
            </h2>
            <span className="text-sm text-gray-500">
              {agentData.profileCompletion}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div
              className="bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen h-3 rounded-full transition-all duration-500"
              style={{ width: `${agentData.profileCompletion}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600">
            {agentData.profileCompletion < 100
              ? "Complete the remaining sections to increase your profile visibility"
              : "Your agent profile is complete and visible to companies!"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Personal Info & Stats */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <Avatar
                name={agentData.fullName}
                size="2xl"
                src={agentData.profilePhoto}
                className="mx-auto mb-4"
              />
              <h2 className="text-xl font-bold text-gray-900">
                {agentData.fullName}
              </h2>
              <p className="text-gray-600 mb-2">
                {agentData.professionalTitle}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {agentData.agencyName}
              </p>
              <Badge variant="primary" className="mb-4">
                {getVerificationStatusText(agentData.verificationStatus)}
              </Badge>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-sm text-gray-600">{agentData.email}</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span className="text-sm text-gray-600">{agentData.phone}</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-sm text-gray-600">
                  {agentData.yearsExperience} years experience
                </span>
              </div>
            </div>
          </div>

          {/* Success Metrics */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Success Metrics
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Placements</span>
                <span className="text-sm font-medium text-gray-900">
                  {agentData.totalPlacements}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Success Rate</span>
                <span className="text-sm font-medium text-gray-900">
                  {agentData.successRate}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Avg Salary Placed</span>
                <span className="text-sm font-medium text-gray-900">
                  {agentData.avgSalaryPlaced}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Client Retention</span>
                <span className="text-sm font-medium text-gray-900">
                  {agentData.clientRetentionRate}%
                </span>
              </div>
            </div>
          </div>

          {/* Specializations */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Specializations
            </h3>
            <div className="flex flex-wrap gap-2">
              {agentData?.specializations?.map((spec, index) => (
                <Badge key={index} variant="secondary" size="sm">
                  {spec}
                </Badge>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Services Offered
            </h3>
            <div className="space-y-2">
              {agentData?.servicesOffered?.map((service, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <svg
                    className="w-4 h-4 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm text-gray-700">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Professional Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Professional Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Professional Summary
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {agentData.professionalSummary}
            </p>
          </div>

          {/* Work Experience */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Work Experience
            </h3>
            <div className="space-y-6">
              {agentData?.experienceDetails?.map((exp, index) => (
                <div
                  key={exp.id}
                  className="border-l-4 border-primary-lightGreen pl-4"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-md font-semibold text-gray-900">
                        {exp.position}
                      </h4>
                      <p className="text-sm text-gray-600">{exp.company}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        {new Date(exp.startDate).toLocaleDateString("en-US", {
                          month: "short",
                          year: "numeric",
                        })}{" "}
                        -
                        {exp.current
                          ? " Present"
                          : new Date(exp.endDate).toLocaleDateString("en-US", {
                              month: "short",
                              year: "numeric",
                            })}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm text-gray-700 mb-2">
                    <p className="font-medium mb-1">Description:</p>
                    <p>{exp.description}</p>
                  </div>
                  <div className="text-sm text-gray-700">
                    <p className="font-medium mb-1">Key Achievements:</p>
                    <p>{exp.achievements}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Certifications & Licenses
            </h3>
            <div className="space-y-4">
              {agentData?.certificationsDetails?.map((cert, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-md font-semibold text-gray-900">
                        {cert.name}
                      </h4>
                      <p className="text-sm text-gray-600">{cert.issuer}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        {new Date(cert.date).toLocaleDateString("en-US", {
                          month: "short",
                          year: "numeric",
                        })}
                        {cert.expiry &&
                          ` - ${new Date(cert.expiry).toLocaleDateString(
                            "en-US",
                            { month: "short", year: "numeric" }
                          )}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        ID: {cert.credentialId}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Services & Capabilities */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Services & Capabilities
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Industries Served
                </h4>
                <p className="text-sm text-gray-900">
                  {agentData.industriesServed}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Geographic Coverage
                </h4>
                <p className="text-sm text-gray-900">
                  {agentData.geographicCoverage}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Fee Structure
                </h4>
                <p className="text-sm text-gray-900">
                  {agentData.feeStructure}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Average Time to Fill
                </h4>
                <p className="text-sm text-gray-900">
                  {agentData.avgTimeToFill}
                </p>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Working Hours
              </h4>
              <p className="text-sm text-gray-900">
                {agentData.startTime} - {agentData.endTime}
              </p>
            </div>

            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Availability
              </h4>
              <div className="flex flex-wrap gap-2">
                {agentData?.availability?.map((avail, index) => (
                  <Badge key={index} variant="outline" size="sm">
                    {avail}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Client Testimonials */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Client Testimonials
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-700 italic">
                "{agentData.testimonials}"
              </p>
            </div>
          </div>

          {/* Notable Placements */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Notable Placements
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {agentData.notablePlacements}
            </p>
          </div>

          {/* Professional References */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Professional References
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Reference 1
                </h4>
                <div className="text-sm text-gray-900">
                  <p className="font-medium">{agentData.ref1Name}</p>
                  <p>{agentData.ref1Company}</p>
                  <p className="text-gray-600">{agentData.ref1Email}</p>
                  <p className="text-gray-600">{agentData.ref1Phone}</p>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Reference 2
                </h4>
                <div className="text-sm text-gray-900">
                  <p className="font-medium">{agentData.ref2Name}</p>
                  <p>{agentData.ref2Company}</p>
                  <p className="text-gray-600">{agentData.ref2Email}</p>
                  <p className="text-gray-600">{agentData.ref2Phone}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Account Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Account Settings
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    Profile Visibility
                  </h4>
                  <p className="text-sm text-gray-500">
                    Make profile visible to companies
                  </p>
                </div>
                <div
                  className={`w-11 h-6 rounded-full ${
                    agentData.profileVisibility ? "bg-green-500" : "bg-gray-200"
                  } flex items-center justify-end px-1`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    Email Notifications
                  </h4>
                  <p className="text-sm text-gray-500">
                    Receive notifications about opportunities
                  </p>
                </div>
                <div
                  className={`w-11 h-6 rounded-full ${
                    agentData.emailNotifications
                      ? "bg-green-500"
                      : "bg-gray-200"
                  } flex items-center justify-end px-1`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentProfileSummary;
